// #351, brief §5.4 and §15 rows 29 and 33: the one generator of a program
// model. The athlete's choices (level, minutes, sessions a week, blocks, the
// exercise of each row) pick the rows and their rules; each rule writes its
// cells (rowsForRule), and every block after the first is flow 9
// (addNextBlock). Keep free of React and any UI import.
import type { EffortScale } from "./effortScale.js";
import type { Locale } from "./locale.js";
import { pickLocalizedText } from "./localizedText.js";
import { blankSlot, type Draft, type SlotDraft } from "./programEdit.js";
import type { ModelLevel, ModelRow, ProgramModel, SessionMinutes } from "./programModel.js";
import { effortOn, harderSign, type ProgressionRule, type Sequence } from "./programProgression.js";
import { rowsForRule } from "./programProgressionApply.js";
import { addNextBlock } from "./programProgressionRounds.js";
import type { RoundType } from "./schemas/programV3.js";
import { uuidv4 } from "./uuid.js";

export type ModelChoices = {
  name: string;
  level: ModelLevel;
  minutes: SessionMinutes;
  /** One of the model's values: the caller offers only those. */
  sessionsPerWeek: number;
  /** How many times the round pattern runs; each block after the first
   *  starts again a step higher. */
  blocks: number;
  /** A row's exercise, by row id. Only the row's own alternatives count. */
  exercisePicks?: Record<string, string>;
  effortScale: EffortScale;
  /** The language the program is written in (Draft.editLocale). */
  editLocale?: Locale | null;
  /** The catalog name of a slug, stored with the slot. */
  exerciseName?: (slug: string) => string | undefined;
};

/** A model's sequence of RPE values, on `scale`. A step or offset turns
 *  sign on RIR, where harder is down. */
const seqOnScale = (seq: Sequence, scale: EffortScale): Sequence => {
  const v = (x: number) => effortOn(x, scale);
  const d = (x: number) => (x === 0 ? 0 : x * harderSign(scale));
  switch (seq.kind) {
    case "constant":
      return { kind: "constant", value: v(seq.value) };
    case "linear":
      return { kind: "linear", start: v(seq.start), step: d(seq.step), ...(seq.cap !== undefined ? { cap: v(seq.cap) } : {}) };
    case "ramp":
      return { ...seq, from: v(seq.from), to: v(seq.to) };
    case "cycle":
      return { kind: "cycle", values: seq.values.map(v) };
    case "blocks":
      return { kind: "blocks", blocks: seq.blocks.map((b) => ({ ...b, seq: seqOnScale(b.seq, scale) })) };
  }
};

/** A model rule on the athlete's effort scale. Models store effort in RPE
 *  (§15 row 29); any other axis is left as it is. */
export const ruleOnScale = (rule: ProgressionRule, scale: EffortScale): ProgressionRule => {
  if (rule.axis !== "rpe" || scale === "rpe") return rule;
  const d = (x: number) => (x === 0 ? 0 : x * harderSign(scale));
  const { perSet, threshold, test, deload } = rule;
  return {
    ...rule,
    axis: scale,
    value: rule.value && seqOnScale(rule.value, scale),
    ...(perSet ? { perSet: { ...perSet, ...(perSet.value ? { value: perSet.value.map(d) } : {}) } } : {}),
    ...(threshold
      ? { threshold: { ...threshold, loadStart: effortOn(threshold.loadStart, scale), loadStep: d(threshold.loadStep) } }
      : {}),
    ...(test ? { test: { ...test, ...(test.delta === undefined ? {} : { delta: d(test.delta) }) } } : {}),
    ...(deload ? { deload: { ...deload, ...(deload.delta === undefined ? {} : { delta: d(deload.delta) }) } } : {}),
  };
};

/** One set fewer, never below one: the per-set values lose their last entry,
 *  and a threshold's set ceiling comes down with it. */
export const oneSetFewer = (rule: ProgressionRule): ProgressionRule => {
  const sets = Math.max(1, rule.sets - 1);
  if (sets === rule.sets) return rule;
  const { perSet, threshold } = rule;
  return {
    ...rule,
    sets,
    ...(perSet
      ? {
          perSet: {
            ...(perSet.reps ? { reps: perSet.reps.slice(0, sets) } : {}),
            ...(perSet.value ? { value: perSet.value.slice(0, sets) } : {}),
          },
        }
      : {}),
    ...(threshold?.setsMax !== undefined
      ? { threshold: { ...threshold, setsMax: Math.max(sets, threshold.setsMax - 1) } }
      : {}),
  };
};

type ModelEntry = NonNullable<ModelRow["byLevel"][ModelLevel]>;

/** The row's entry at `level`; null when the row is not there at these
 *  choices (no entry for the level, or an optional row under 75 minutes). */
export const modelRowAt = (row: ModelRow, level: ModelLevel, minutes: SessionMinutes): ModelEntry | null => {
  const entry = row.byLevel[level];
  if (!entry) return null;
  return row.weight === "optional" && minutes < 75 ? null : entry;
};

/** The row's exercise: the pick when it is one of the row's own, else the
 *  default (the first). */
export const modelRowExercise = (entry: ModelEntry, pick: string | undefined): string =>
  pick !== undefined && entry.exercises.includes(pick) ? pick : entry.exercises[0]!;

/** The slot a row gives at the athlete's choices, or null (modelRowAt). */
const slotOf = (row: ModelRow, choices: ModelChoices, types: readonly RoundType[]): SlotDraft | null => {
  const entry = modelRowAt(row, choices.level, choices.minutes);
  if (!entry) return null;
  const exerciseId = modelRowExercise(entry, choices.exercisePicks?.[row.id]);
  let rule = ruleOnScale(entry.rule, choices.effortScale);
  if (row.weight === "accessory" && choices.minutes === 45) rule = oneSetFewer(rule);
  return {
    ...blankSlot(types.length),
    exerciseId,
    exerciseName: choices.exerciseName?.(exerciseId),
    setRows: rowsForRule(rule, types, types.length),
  };
};

/** A new program from `model` at the athlete's choices. Day names in the
 *  edit language, else English. */
export const buildModelDraft = (model: ProgramModel, choices: ModelChoices): Draft => {
  const types = model.roundPattern;
  const lang = choices.editLocale ?? "en";
  let draft: Draft = {
    name: choices.name,
    description: "",
    ...(choices.editLocale !== undefined ? { editLocale: choices.editLocale } : {}),
    ...(choices.editLocale ? { declaredAuthorLocale: choices.editLocale } : {}),
    status: "draft",
    cycleWeeks: types.length,
    sessionsPerWeek: choices.sessionsPerWeek,
    roundTypes: [...types],
    sessions: model.days.map((day) => ({
      id: uuidv4(),
      name: pickLocalizedText(day.name, lang),
      notes: "",
      slots: day.rows.flatMap((row) => slotOf(row, choices, types) ?? []),
    })),
    assignedTo: [],
    assigneeEmails: [],
  };
  for (let b = 1; b < choices.blocks; b += 1) {
    const next = addNextBlock(draft, { rounds: types.length, types });
    if (!next.ok) break;
    draft = next.draft;
  }
  return draft;
};
